import {
  type Dispatch,
  type PointerEvent,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { type FlowChartViewport } from './FlowChart';

const INITIAL_VIEWPORT: FlowChartViewport = {
  scale: 1,
  x: 0,
  y: 0,
};

type UseFlowChartPanZoomOptions = {
  svgRef: RefObject<SVGSVGElement | null>;
  graphWidth: number;
  graphHeight: number;
  minScale: number;
  maxScale: number;
  /**
   * When set, pan/zoom writes `transform` on this `<g>` and skips React
   * re-renders for viewport changes. Use for large graphs where reconciling
   * every node/edge on each pointer-move frame is too expensive.
   *
   * With this ref, returned `viewport` is not kept in sync during gestures —
   * read the live transform from the group (or call `setViewport`) instead.
   */
  viewportGroupRef?: RefObject<SVGGElement | null>;
  /**
   * When true, plain wheel/trackpad input zooms around the pointer.
   * When false (default), only Ctrl/Meta-wheel zooms; plain wheel pans.
   */
  wheelZooms?: boolean;
};

type PanZoomHandlers = {
  onPointerDown: (event: PointerEvent<SVGSVGElement>) => void;
  onPointerMove: (event: PointerEvent<SVGSVGElement>) => void;
  onPointerCancel: (event: PointerEvent<SVGSVGElement>) => void;
  onPointerUp: (event: PointerEvent<SVGSVGElement>) => void;
};

export type UseFlowChartPanZoomResult = {
  viewport: FlowChartViewport;
  setViewport: Dispatch<SetStateAction<FlowChartViewport>>;
  isPanning: boolean;
  /**
   * Wraps `fn` so it becomes a no-op when called immediately after a
   * pointer-drag ends. Pass this to `onPress` handlers on nodes/edges so
   * clicks that finish a drag don't open detail panels.
   *
   * The returned wrapper is stable across renders (safe in `useMemo` deps).
   */
  suppressAfterDrag: (fn: () => void) => () => void;
  /** Zoom anchored to a point in SVG-space coordinates. */
  zoomAtPoint: (
    point: { x: number; y: number },
    scaleMultiplier: number
  ) => void;
  /** Convenience wrapper: zooms anchored to the centre of the graph. */
  zoomAtCenter: (scaleMultiplier: number) => void;
  /** Spread these directly onto the `<FlowChart>` (or root `<svg>`) element. */
  panZoomHandlers: PanZoomHandlers;
};

export const viewportTransform = (viewport: FlowChartViewport) =>
  `translate(${viewport.x} ${viewport.y}) scale(${viewport.scale})`;

/**
 * Converts a pointer/mouse event position into SVG coordinate space.
 * Pass `{ width: graph.width, height: graph.height }` from your `FlowChartGraph`.
 * Use this when implementing pan/zoom with the `viewport` prop.
 */
export const svgPointFromEvent = (
  event: Pick<MouseEvent | PointerEvent, 'clientX' | 'clientY'>,
  svg: SVGSVGElement,
  graphSize: { width: number; height: number }
) => {
  const rect = svg.getBoundingClientRect();

  return {
    x: ((event.clientX - rect.left) / rect.width) * graphSize.width,
    y: ((event.clientY - rect.top) / rect.height) * graphSize.height,
  };
};

/**
 * Encapsulates pointer-driven pan and wheel/trackpad zoom for a `<FlowChart>`
 * SVG. Pass the same `svgRef` that is forwarded to `<FlowChart ref={...}>`.
 *
 * The wheel listener is registered as non-passive so `preventDefault()` can
 * suppress the browser's native scroll/pinch-zoom.
 *
 * Pass `viewportGroupRef` to mutate that group's transform imperatively and
 * avoid a React re-render on every pan/zoom frame.
 */
export const useFlowChartPanZoom = ({
  svgRef,
  graphWidth,
  graphHeight,
  minScale,
  maxScale,
  viewportGroupRef,
  wheelZooms = false,
}: UseFlowChartPanZoomOptions): UseFlowChartPanZoomResult => {
  const viewportRef = useRef<FlowChartViewport>(INITIAL_VIEWPORT);
  const [viewport, setViewportState] =
    useState<FlowChartViewport>(INITIAL_VIEWPORT);
  const [isPanning, setIsPanning] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
    moved: boolean;
  } | null>(null);
  const movedRef = useRef(false);

  const setViewport = useCallback(
    (update: SetStateAction<FlowChartViewport>) => {
      const current = viewportRef.current;
      const next = typeof update === 'function' ? update(current) : update;
      viewportRef.current = next;

      const group = viewportGroupRef?.current;
      if (group) {
        group.setAttribute('transform', viewportTransform(next));
        return;
      }

      setViewportState(next);
    },
    [viewportGroupRef]
  );

  useLayoutEffect(() => {
    const group = viewportGroupRef?.current;
    if (!group) {
      return;
    }
    group.setAttribute('transform', viewportTransform(viewportRef.current));
  }, [viewportGroupRef]);

  const zoomAtPoint = (
    point: { x: number; y: number },
    scaleMultiplier: number
  ) => {
    setViewport(current => {
      const nextScale = Math.min(
        maxScale,
        Math.max(minScale, current.scale * scaleMultiplier)
      );
      const graphX = (point.x - current.x) / current.scale;
      const graphY = (point.y - current.y) / current.scale;

      return {
        scale: nextScale,
        x: point.x - graphX * nextScale,
        y: point.y - graphY * nextScale,
      };
    });
  };

  const zoomAtCenter = (scaleMultiplier: number) => {
    zoomAtPoint({ x: graphWidth / 2, y: graphHeight / 2 }, scaleMultiplier);
  };

  const handleWheel = (event: WheelEvent) => {
    event.preventDefault();

    const svg = svgRef.current;
    if (!svg) {
      return;
    }
    const rect = svg.getBoundingClientRect();

    if (wheelZooms || event.ctrlKey || event.metaKey) {
      const point = svgPointFromEvent(event, svg, {
        width: graphWidth,
        height: graphHeight,
      });
      zoomAtPoint(point, Math.exp(-event.deltaY * 0.006));
      return;
    }

    const deltaX = (event.deltaX / rect.width) * graphWidth;
    const deltaY = (event.deltaY / rect.height) * graphHeight;

    setViewport(current => ({
      ...current,
      x: current.x - deltaX,
      y: current.y - deltaY,
    }));
  };

  // Keep a ref so the effect closure always calls the latest handleWheel
  // without needing to re-register the listener on every render.
  const handleWheelRef = useRef(handleWheel);
  handleWheelRef.current = handleWheel;

  // svgRef is a stable ref object — the listener is kept current via handleWheelRef.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }
    const listener = (e: WheelEvent) => handleWheelRef.current(e);
    svg.addEventListener('wheel', listener, { passive: false });
    return () => svg.removeEventListener('wheel', listener);
  }, []);

  const handlePointerDown = (event: PointerEvent<SVGSVGElement>) => {
    if (!event.isPrimary) {
      return;
    }

    const target = event.target;
    if (target instanceof Element && target.closest('[role="button"]')) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      moved: false,
    };
    setIsPanning(true);
  };

  const handlePointerMove = (event: PointerEvent<SVGSVGElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const deltaX = ((event.clientX - drag.clientX) / rect.width) * graphWidth;
    const deltaY = ((event.clientY - drag.clientY) / rect.height) * graphHeight;

    if (
      Math.abs(event.clientX - drag.clientX) +
        Math.abs(event.clientY - drag.clientY) >
      2
    ) {
      drag.moved = true;
      movedRef.current = true;
    }

    drag.clientX = event.clientX;
    drag.clientY = event.clientY;

    setViewport(current => ({
      ...current,
      x: current.x + deltaX,
      y: current.y + deltaY,
    }));
  };

  const finishPointerPan = (event: PointerEvent<SVGSVGElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    dragRef.current = null;
    setIsPanning(false);

    if (drag.moved) {
      window.setTimeout(() => {
        movedRef.current = false;
      }, 0);
    }
  };

  // Stable across renders — movedRef is declared here with useRef, so Biome
  // correctly treats .current access inside useCallback as safe.
  const suppressAfterDrag = useCallback(
    (fn: () => void) => () => {
      if (movedRef.current) {
        return;
      }
      fn();
    },
    []
  );

  return {
    viewport,
    setViewport,
    isPanning,
    suppressAfterDrag,
    zoomAtPoint,
    zoomAtCenter,
    panZoomHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerCancel: finishPointerPan,
      onPointerUp: finishPointerPan,
    },
  };
};
